import React from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import type { ComplianceWorkflowPayload } from '../../services/complianceWorkflowApi'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

interface Props {
  workflow: ComplianceWorkflowPayload
  selectedIds: string[]
  onToggleSystem: (id: string) => void
  organizationWide: boolean
  onOrganizationWideChange: (v: boolean) => void
  onSave: () => void
  onFinalize: () => void
  saving?: boolean
}

export default function ScopeBuilderWorkspace({
  workflow,
  selectedIds,
  onToggleSystem,
  organizationWide,
  onOrganizationWideChange,
  onSave,
  onFinalize,
  saving,
}: Props) {
  const { t } = useI18n()
  const systems = workflow.informationSystems || []
  const scopeAuthority = (workflow as { scopeAuthority?: { scopeRevision?: number; scopeStatus?: string } })
    .scopeAuthority
  const scopeAuthorityV1 = featureFlags.COMPLIANCE_SCOPE_AUTHORITY_V1
  const selectedSet = new Set(selectedIds)

  return (
    <div className={wfStyles.innerStack}>
      <GovernanceListingSection title={t('complianceWorkflow.scopeBuilder', 'Scope Builder')}>
        <label className={wfStyles.checkboxLabel}>
          <input
            type="checkbox"
            checked={organizationWide}
            onChange={(e) => onOrganizationWideChange(e.target.checked)}
          />
          {t('complianceTopology.organizationWide', 'Organization-wide applicability')}
        </label>
        {scopeAuthorityV1 && organizationWide && !selectedIds.length ? (
          <p className={wfStyles.itemMeta}>
            {t(
              'complianceWorkflow.orgWideNoSystems',
              'Organization-wide controls apply, but no information systems are currently selected in workflow scope.',
            )}
          </p>
        ) : null}
        {scopeAuthorityV1 && scopeAuthority?.scopeRevision != null ? (
          <p className={wfStyles.itemMeta}>
            {t('complianceWorkflow.scopeRevision', 'Scope revision')}: {scopeAuthority.scopeRevision}
            {scopeAuthority.scopeStatus ? ` · ${scopeAuthority.scopeStatus}` : ''}
          </p>
        ) : null}
        <div className={wfStyles.listScroll}>
          {systems.map((sys) => {
            const inWorkflowScope = selectedSet.has(sys.id)
            return (
            <label
              key={sys.id}
              className={wfStyles.listItemSelectable}
              style={scopeAuthorityV1 && !inWorkflowScope ? { opacity: 0.65 } : undefined}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(sys.id)}
                onChange={() => onToggleSystem(sys.id)}
              />
              <div>
                <p className={wfStyles.itemTitle}>
                  {sys.name}
                  {scopeAuthorityV1 ? (
                    <span className={wfStyles.itemMeta}>
                      {' '}
                      —{' '}
                      {inWorkflowScope
                        ? t('complianceWorkflow.inWorkflowScope', 'In workflow scope')
                        : t('complianceWorkflow.notInWorkflowScope', 'Not in workflow scope')}
                    </span>
                  ) : null}
                </p>
                {sys.environment ? (
                  <p className={wfStyles.itemMeta}>({sys.environment})</p>
                ) : null}
                {sys.dataCategories?.length ? (
                  <p className={wfStyles.itemMeta}>{sys.dataCategories.join(', ')}</p>
                ) : null}
              </div>
            </label>
          )})}
        </div>
        <div className={wfStyles.actionsRow}>
          <Button type="button" size="small" color="brand" disabled={saving} onClick={onSave}>
            {t('common.save', 'Save scope')}
          </Button>
          <Button
            type="button"
            size="small"
            color="outline"
            disabled={saving || !selectedIds.length}
            onClick={onFinalize}
          >
            {t('complianceWorkflow.finalizeScope', 'Finalize Scope')}
          </Button>
        </div>
      </GovernanceListingSection>

      {(() => {
        const detected = workflow.detectedApplicability || []
        const scopeDef = workflow.scopeDefinition as
          | { informationSystemIds?: string[]; selectedInformationSystemIds?: string[] }
          | undefined
        const scopeIdsFromServer = [
          ...(scopeDef?.selectedInformationSystemIds || []),
          ...(scopeDef?.informationSystemIds || []),
        ]
        const allowedIds = new Set(
          (selectedIds.length ? selectedIds : scopeIdsFromServer).map(String).filter(Boolean),
        )
        const filtered =
          allowedIds.size > 0
            ? detected.filter(
                (d) =>
                  !('systemId' in d && (d as { systemId?: string }).systemId) ||
                  allowedIds.has(String((d as { systemId?: string }).systemId)),
              )
            : scopeAuthorityV1
              ? detected.filter((d) => !('systemId' in d && (d as { systemId?: string }).systemId))
              : detected
        return filtered.length ? (
        <GovernanceListingSection
          title={t('complianceWorkflow.applicabilityDetected', 'Applicability Detected')}
        >
          <p className={wfStyles.itemMeta}>
            {t('complianceWorkflow.scopedSystemsOnly', 'Scoped systems only')}
            {allowedIds.size > 0 ? ` (${allowedIds.size})` : ''}
          </p>
          <ul className={wfStyles.innerStack}>
            {filtered.map((d, i) => (
              <li key={i} className={wfStyles.listItem} title={d.message}>
                <p className={wfStyles.itemTitle}>{d.label}</p>
                <p className={wfStyles.itemMeta}>{d.message}</p>
              </li>
            ))}
          </ul>
        </GovernanceListingSection>
        ) : null
      })()}
    </div>
  )
}
